import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "@phosphor-icons/react";
import { GoogleLogin } from '@react-oauth/google';

export default function Login() {
  const { login, googleLogin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) nav(loc.state?.from || "/dashboard");
    else setErr(r.error);
  };

  const handleGoogleSuccess = async (response) => {
    setBusy(true);
    setErr("");
    const r = await googleLogin(response.credential);
    setBusy(false);
    if (r.ok) nav(loc.state?.from || "/dashboard");
    else setErr(r.error);
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-12">
      <div className="hidden lg:flex col-span-5 bg-foreground text-background p-12 flex-col justify-between">
        <div className="eyebrow opacity-80">Inkspot — Sign In</div>
        <div>
          <h1 className="font-serif text-6xl font-black leading-[0.95]">
            Welcome<br />back to the<br /><span className="italic font-medium">desk.</span>
          </h1>
          <p className="mt-6 max-w-sm text-sm opacity-80 leading-relaxed">
            Pick up where you left off. Drafts, reads, and the next sentence are waiting.
          </p>
        </div>
        <div className="text-xs opacity-70">© {new Date().getFullYear()} Inkspot</div>
      </div>
      <div className="col-span-12 lg:col-span-7 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="eyebrow text-muted-foreground mb-3">Sign in</div>
          <h2 className="font-serif text-4xl font-black tracking-tight">Continue writing.</h2>
          <p className="mt-2 text-sm text-muted-foreground">No account? <Link to="/register" className="link-underline text-foreground">Create one</Link>.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="eyebrow text-muted-foreground">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none mt-2 h-12" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="eyebrow text-muted-foreground">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none mt-2 h-12" data-testid="login-password-input" />
            </div>
            {err ? <div className="text-sm text-destructive" data-testid="login-error">{err}</div> : null}
            <Button type="submit" disabled={busy} className="rounded-none h-12 w-full bg-foreground text-background hover:bg-foreground/90" data-testid="login-submit-button">
              {busy ? "Signing in..." : (<>Sign in <ArrowRight size={16} weight="bold" className="ml-2" /></>)}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground eyebrow">Or continue with</span></div>
            </div>

            <div className="w-full">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setErr("Google Sign In failed")}
                useOneTap
                theme="outline"
                shape="square"
                width="100%"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
